/**
 * Add-Equipment dialog.
 *
 * Form layout, in render order:
 *   1. **Location** — anchor asset; it also decides which trades may be created
 *      here, so it drives the Type picker's allow-list.
 *   2. **Type** — a single AssetTypePicker rooted at the `equipment` subtree:
 *      the user can drill down trade → leaf or just type to search across all
 *      levels of the equipment hierarchy.
 *   3. **Quantity** — number, stored on the freshly-created asset row (batch
 *      types only).
 *   4. **Commissioning date** — the asset's `start_date` column, the single
 *      source of truth for when a piece of equipment was put into service
 *      (spec 2026-09-03-02). Pre-seeded with the location's construction year.
 *   5. **More properties** — block of dynamic fields driven by the selected
 *      type's `property_definitions`.
 *
 * The asset name is derived automatically from the picked type's label — there
 * is no manual name field.
 *
 * Submission composes `POST /assets` (with `quantity` and `start_date`) + N
 * `POST /assets/{uid}/properties` per filled property — no new endpoint, all
 * routes are existing ones.
 */

import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { mdiLoading } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { ApiSearchableSelect, type ApiSearchableSelectOption } from '../ApiSearchableSelect'
import {
  useAsset,
  useAssetProperties,
  useAssetTypeInheritedProperties,
  useAssetTypes,
  useAssets,
  useChildAssetTypes,
  useCreateAsset,
  usePropertyEnumValues,
  type PropertyDefinition,
} from '../../hooks/useQueries'
import { refreshEquipmentTab } from '../../hooks/useEquipmentTab'
import { useApi } from '../../api/hooks'
import {
  assetTypeLabel,
  composePropertyWrites,
  findEquipmentRoot,
  isBatchType,
  isMonolithicType,
  selectDynamicProperties,
  validate,
  type AssetTypeMeta,
} from './add-equipment'
import { AssetTypePicker } from '../asset-type-picker/AssetTypePicker'
import { componentErrorMessage, refreshComponents, useAssetComponents } from '../../hooks/useComponents'
import { EmptyState } from '../ui/empty-state'

interface AddEquipmentDialogProps {
  org: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Asset under which the new equipment is created (location default). */
  parentAssetUid: string
  /** Pre-selected trade UID (e.g. when opened from a trade accordion `+`). */
  preselectedTradeUid?: string
  /** Locale-resolved label of the parent asset, for the autosuggested name. */
  parentAssetLabel?: string
  /** Called after a successful creation, before the dialog closes. */
  onCreated?: (assetUid: string) => void
  /**
   * "Add a component" (spec 2026-10-27-17): the equipment the new asset is
   * part of. The location defaults to the assembly's tree parent and the
   * submit sends `part_of_asset_uid`.
   */
  assemblyUid?: string
  /** Name of the assembly, for the dialog title. */
  assemblyLabel?: string
  /** Component types usual in the assembly, offered first. */
  suggestedTypeCodes?: string[]
}

export function AddEquipmentDialog({
  org,
  open,
  onOpenChange,
  parentAssetUid,
  preselectedTradeUid,
  parentAssetLabel,
  onCreated,
  assemblyUid,
  assemblyLabel,
  suggestedTypeCodes,
}: AddEquipmentDialogProps) {
  const { t, i18n } = useTranslation('assets')
  const queryClient = useQueryClient()
  const { request } = useApi()

  // --- Form state ------------------------------------------------------

  /** UID of the picked asset_type. Empty string means "not yet picked". */
  const [typeUidState, setTypeUidState] = useState<string>('')
  const [quantity, setQuantity] = useState<number | null>(1)
  const [locationUid, setLocationUid] = useState<string>(parentAssetUid)
  /** `assets.start_date` of the equipment to create — ISO `YYYY-MM-DD` or ''. */
  const [commissionedOn, setCommissionedOn] = useState<string>('')
  const [propertyValues, setPropertyValues] = useState<Record<string, unknown>>({})
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  // --- Data ------------------------------------------------------------

  const { data: allAssetTypesRaw = [] } = useAssetTypes(org)
  const allAssetTypes = allAssetTypesRaw as unknown as AssetTypeMeta[]
  const equipmentRoot = useMemo(() => findEquipmentRoot(allAssetTypes), [allAssetTypes])

  // A component goes where its assembly is by default (spec 2026-10-27-17).
  const { data: assemblyAsset } = useAsset(org, assemblyUid ?? '')
  const assemblyParentUid = (assemblyAsset as { parent_asset_uid?: string | null } | undefined)?.parent_asset_uid ?? undefined
  const { data: assemblyView } = useAssetComponents(org, assemblyUid, {
    enabled: open && !!assemblyUid && suggestedTypeCodes === undefined,
  })
  const suggestedTypes = useMemo<AssetTypeMeta[]>(() => {
    const codes = suggestedTypeCodes ?? assemblyView?.suggested_type_codes ?? []
    return codes
      .map((code) => allAssetTypes.find((at) => at.code === code))
      .filter((at): at is AssetTypeMeta => !!at && !at.code.endsWith('_custom'))
  }, [suggestedTypeCodes, assemblyView?.suggested_type_codes, allAssetTypes])
  const defaultLocationUid = assemblyUid ? assemblyParentUid ?? parentAssetUid : parentAssetUid
  const { data: assemblyParent } = useAsset(org, assemblyUid ? assemblyParentUid ?? '' : '')
  const locationDefaultLabel =
    assemblyUid && assemblyParentUid ? (assemblyParent as { name?: string } | undefined)?.name : parentAssetLabel

  // Trades that can be located under the chosen location. Fail open: when the
  // location's asset type can't be resolved, `parentAssetTypeUid` is undefined
  // and we leave the picker unrestricted.
  const { data: allowedChildTypes = [], parentAssetTypeUid } = useChildAssetTypes(
    org,
    locationUid,
  )
  const allowedTradeUids = useMemo<string[] | undefined>(() => {
    if (!parentAssetTypeUid) return undefined
    return (allowedChildTypes as Array<{ uid: string }>).map((at) => at.uid)
  }, [parentAssetTypeUid, allowedChildTypes])

  // Reset on open / preselection change so reopening from a different trade
  // accordion picks up cleanly. Seeds the type with the preselected trade.
  useEffect(() => {
    if (!open) return
    setTypeUidState(preselectedTradeUid ?? '')
    setQuantity(1)
    setLocationUid(defaultLocationUid)
    setPropertyValues({})
    setError(null)
    setFieldErrors({})
  }, [open, preselectedTradeUid, defaultLocationUid])

  /** The asset_type that will actually be created. */
  const selectedType = useMemo<AssetTypeMeta | null>(() => {
    if (!typeUidState) return null
    return allAssetTypes.find((at) => at.uid === typeUidState) ?? null
  }, [allAssetTypes, typeUidState])
  const typeUid = selectedType?.uid ?? ''

  const { data: typeProperties = [], isLoading: typePropsLoading } =
    useAssetTypeInheritedProperties(org, typeUid)
  // Structure / roof / facade items are single building elements — no quantity.
  const monolithic = useMemo(() => isMonolithicType(selectedType), [selectedType])
  const dynamicProps = useMemo(
    () => selectDynamicProperties(typeProperties),
    [typeProperties],
  )

  // Shortcut: default the commissioning year to the location's construction
  // year (equipment is assumed as old as the building unless stated otherwise).
  const { data: locationProps = [] } = useAssetProperties(org, locationUid)
  const constructionYear = useMemo<number | null>(() => {
    const match = (locationProps as Array<Record<string, unknown>>).find((v) => {
      const def = v.property_definition as { code?: string } | undefined
      const code = def?.code ?? (v.code as string | undefined)
      return code === 'construction_date' || code === 'year_of_construction'
    })
    const raw = match?.value
    if (raw == null) return null
    const m = String(raw).match(/\d{4}/)
    return m ? parseInt(m[0], 10) : null
  }, [locationProps])
  // Seed the commissioning date with 1 January of the construction year,
  // unless the user has already entered something. The construction year is a
  // year, so the day is a convention — the same one the retired
  // `commissioning_year` property implied.
  useEffect(() => {
    if (constructionYear == null) return
    setCommissionedOn((prev) => (prev === '' ? `${constructionYear}-01-01` : prev))
  }, [constructionYear])

  // --- Mutations -------------------------------------------------------

  const createMutation = useCreateAsset(org)

  // --- Handlers --------------------------------------------------------

  const handlePickType = (uid: string) => {
    setTypeUidState(uid)
    // Reset dynamic properties: their definitions are scoped to the picked
    // type, so old values are no longer guaranteed to be valid.
    setPropertyValues({})
    setFieldErrors({})
  }

  const handlePropertyChange = (propertyUid: string, value: unknown) => {
    setPropertyValues((prev) => ({ ...prev, [propertyUid]: value }))
  }

  const handleSubmit = async () => {
    setError(null)
    setFieldErrors({})

    const result = validate({
      typeUid,
      quantity,
      locationUid,
      assetType: selectedType,
    })
    if (!result.ok) {
      setFieldErrors(result.errors)
      return
    }

    setSubmitting(true)
    try {
      const created = await createMutation.mutateAsync({
        name: selectedType ? assetTypeLabel(selectedType, i18n.language) : '',
        asset_type_uid: typeUid,
        parent_asset_uid: locationUid,
        quantity: (quantity ?? 1) as never,
        start_date: commissionedOn || null,
        ...(assemblyUid ? { part_of_asset_uid: assemblyUid } : {}),
      } as never)
      const createdUid = (created as { uid?: string } | undefined)?.uid

      const writes = composePropertyWrites(propertyValues)
      if (createdUid && writes.length > 0) {
        // Use the raw API client so we can target the freshly-created asset's
        // UID — the typed mutation hook is bound to a specific assetUid at
        // construction time.
        for (const w of writes) {
          await request(
            '/api/v1/organizations/{orgCode}/assets/{assetUid}/properties',
            'POST',
            {
              params: { path: { orgCode: org, assetUid: createdUid } },
              body: { properties: [w] },
            },
          )
        }
      }

      // Refresh the equipment tab so the new row appears, plus the parent
      // asset's children list.
      refreshEquipmentTab(queryClient, org, parentAssetUid)
      if (assemblyUid) refreshComponents(queryClient, org)

      if (createdUid && onCreated) onCreated(createdUid)
      onOpenChange(false)
    } catch (err) {
      setError(
        assemblyUid
          ? componentErrorMessage(err, t)
          : err instanceof Error ? err.message : t('add_equipment.error_failed'),
      )
    } finally {
      setSubmitting(false)
    }
  }

  // --- Rendering -------------------------------------------------------

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="add-equipment-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {assemblyUid
              ? t('components.add_title', { name: assemblyLabel || (assemblyAsset as { name?: string } | undefined)?.name || '' })
              : t('add_equipment.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Location — comes first: it determines which equipment types (trades)
              can be created here, so it drives the Type picker's allow-list. */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t('add_equipment.location_label')} <span className="text-destructive">*</span>
            </label>
            <LocationPicker
              org={org}
              value={locationUid}
              defaultLabel={locationDefaultLabel}
              onChange={setLocationUid}
              disabled={submitting}
            />
            {fieldErrors.location && (
              <p className="mt-1 text-xs text-destructive">{t(fieldErrors.location)}</p>
            )}
          </div>

          {/* Type — single picker over the equipment subtree, restricted to the
              trades allowed under the chosen location. */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t('add_equipment.type_label')} <span className="text-destructive">*</span>
            </label>
            {suggestedTypes.length > 0 && (
              <div className="mb-2" data-testid="add-equipment-suggested-types">
                <p className="mb-1 text-xs font-medium text-muted-foreground">{t('components.suggested_group')}</p>
                <div className="flex flex-wrap gap-1.5">
                  {suggestedTypes.map((at) => (
                    <Button
                      key={at.uid}
                      type="button"
                      size="sm"
                      variant={typeUid === at.uid ? 'default' : 'outline'}
                      onClick={() => handlePickType(at.uid)}
                      disabled={submitting}
                      data-testid={`add-equipment-suggested-type-${at.code}`}
                    >
                      {assetTypeLabel(at, i18n.language)}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            {!equipmentRoot ? (
              <p className="text-sm text-muted-foreground italic">
                {t('add_equipment.type_disabled')}
              </p>
            ) : (
              <AssetTypePicker
                org={org}
                value={typeUid}
                onChange={handlePickType}
                restrictToDescendantsOf={equipmentRoot.uid}
                allowedTopLevelUids={allowedTradeUids}
                defaultOpen={!!preselectedTradeUid}
                disabled={submitting}
                placeholder={t('add_equipment.type_placeholder')}
                ariaLabel={t('add_equipment.type_label')}
                triggerTestId="add-equipment-type-picker"
              />
            )}
            {fieldErrors.type && (
              <p className="mt-1 text-xs text-destructive">{t(fieldErrors.type)}</p>
            )}
          </div>

          {/* Quantity — only rendered for batch types (asset_type.properties.multiple
              === true). Unit types are created one-per-row and the dialog defaults
              quantity to 1 internally; surfacing an irrelevant field would only
              confuse the user. */}
          {isBatchType(selectedType) && !monolithic && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('add_equipment.quantity_label')} <span className="text-destructive">*</span>
              </label>
              <Input
                type="number"
                min={1}
                max={32767}
                value={quantity ?? ''}
                onChange={(e) => {
                  const n = parseInt(e.target.value, 10)
                  setQuantity(Number.isFinite(n) && n > 0 ? n : null)
                }}
                disabled={submitting}
                data-testid="add-equipment-quantity-input"
                className="w-32"
              />
              {fieldErrors.quantity && (
                <p className="mt-1 text-xs text-destructive">{t(fieldErrors.quantity)}</p>
              )}
            </div>
          )}

          {/* Commissioning date — `assets.start_date`, not a property: the
              column is the single source of truth for when the equipment was
              put into service (spec 2026-09-03-02). */}
          <div>
            <label
              className="block text-sm font-medium text-foreground mb-2"
              htmlFor="add-equipment-commissioned-input"
            >
              {t('add_equipment.commissioned_label')}
            </label>
            <Input
              id="add-equipment-commissioned-input"
              type="date"
              value={commissionedOn}
              onChange={(e) => setCommissionedOn(e.target.value)}
              disabled={submitting}
              data-testid="add-equipment-commissioned-input"
              className="w-48"
            />
            {constructionYear != null && (
              <p className="mt-1 text-xs text-muted-foreground">
                {t('add_equipment.commissioning_construction_hint', { year: constructionYear })}
              </p>
            )}
          </div>

          {/* Type-specific properties — shown directly once a type is picked. */}
          {typeUid && (
            <div className="border-t border-border pt-4" data-testid="add-equipment-dynamic-section">
              {typePropsLoading ? (
                <p className="text-sm text-muted-foreground italic">
                  {t('add_equipment.dynamic_section_loading')}
                </p>
              ) : dynamicProps.length === 0 ? (
                <EmptyState variant="inline" className="py-3" description={t('add_equipment.dynamic_section_empty')} />
              ) : (
                <div className="space-y-3">
                  {dynamicProps.map((def) => (
                    <DynamicField
                      key={def.uid as string}
                      org={org}
                      definition={def}
                      value={propertyValues[def.uid as string]}
                      onChange={(v) => handlePropertyChange(def.uid as string, v)}
                      disabled={submitting}
                      locale={i18n.language}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3 text-sm text-destructive">
              {error}
            </div>
          )}
        </div>

        <DialogFooter className="mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            {t('add_equipment.cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !typeUid}
            data-testid="add-equipment-submit"
          >
            {submitting ? (
              <>
                <AppIcon path={mdiLoading} size="sm" className="mr-2 motion-safe:animate-spin" />
                {t('add_equipment.submitting')}
              </>
            ) : (
              t('add_equipment.submit')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// =============================================================================
// LocationPicker — wraps the existing assets-search select
// =============================================================================

interface LocationPickerProps {
  org: string
  value: string
  defaultLabel?: string
  onChange: (uid: string) => void
  disabled: boolean
}

function LocationPicker({ org, value, defaultLabel, onChange, disabled }: LocationPickerProps) {
  const { request } = useApi()
  const { data: initialAssets = [] } = useAssets(org)

  const initialOptions = useMemo<ApiSearchableSelectOption[]>(() => {
    const opts: ApiSearchableSelectOption[] = (initialAssets as Array<{ uid: string; name?: string | null }>)
      .slice(0, 20)
      .map((a) => ({ value: a.uid, label: a.name || a.uid }))
    if (value && defaultLabel && !opts.some((o) => o.value === value)) {
      opts.unshift({ value, label: defaultLabel })
    }
    return opts
  }, [initialAssets, value, defaultLabel])

  const search = async (q: string): Promise<ApiSearchableSelectOption[]> => {
    try {
      const queryParams: Record<string, string> = { limit: '20' }
      if (q) {
        queryParams.q = q
        queryParams.sort = 'name'
      } else {
        queryParams.sort = '-updated_at'
      }
      const res = await request('/api/v1/organizations/{orgCode}/assets', 'GET', {
        params: { path: { orgCode: org }, query: queryParams },
      })
      const data = ((res as { data?: Array<{ uid: string; name?: string | null }> }).data) ?? []
      return data.map((a) => ({ value: a.uid, label: a.name || a.uid }))
    } catch {
      return []
    }
  }

  return (
    <ApiSearchableSelect
      value={value}
      onValueChange={onChange}
      onSearch={search}
      initialOptions={initialOptions}
      minSearchLength={2}
      disabled={disabled}
      placeholder=""
      searchPlaceholder=""
      emptyMessage=""
    />
  )
}

// =============================================================================
// DynamicField — render the right input for a property_definition
// =============================================================================

interface DynamicFieldProps {
  org: string
  definition: PropertyDefinition
  value: unknown
  onChange: (value: unknown) => void
  disabled: boolean
  locale: string
  /** Optional helper text rendered under the field (e.g. a default-value hint). */
  hint?: string
}

function DynamicField({ org, definition, value, onChange, disabled, locale, hint }: DynamicFieldProps) {
  const dataType = (definition as { data_type?: string }).data_type || 'text'
  const propUid = definition.uid as string
  const name = definition.name as Record<string, string> | undefined
  const label = (name && (name[locale] || name.en || name.fr)) || definition.code || ''
  const isMultipleEnum =
    dataType === 'enum' &&
    (definition.properties as { multiple?: boolean } | undefined)?.multiple === true

  const labelEl = (
    <label className="block text-sm font-medium text-foreground mb-1">{label}</label>
  )

  if (dataType === 'enum') {
    return (
      <div data-testid={`add-equipment-field-${definition.code}`}>
        {labelEl}
        <EnumField
          org={org}
          propUid={propUid}
          value={value}
          onChange={onChange}
          disabled={disabled}
          multiple={isMultipleEnum}
          locale={locale}
        />
      </div>
    )
  }

  if (dataType === 'boolean') {
    return (
      <div data-testid={`add-equipment-field-${definition.code}`}>
        {labelEl}
        <select
          value={value === true ? 'true' : value === false ? 'false' : ''}
          onChange={(e) => {
            const v = e.target.value
            onChange(v === 'true' ? true : v === 'false' ? false : null)
          }}
          disabled={disabled}
          className="block w-full px-3 py-2 border border-input bg-background text-foreground rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
        >
          <option value="">—</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      </div>
    )
  }

  if (dataType === 'number') {
    return (
      <div data-testid={`add-equipment-field-${definition.code}`}>
        {labelEl}
        <Input
          type="number"
          value={value === null || value === undefined ? '' : String(value)}
          onChange={(e) => {
            const v = e.target.value
            if (v === '') onChange(null)
            else {
              const n = Number(v)
              if (!Number.isNaN(n)) onChange(n)
            }
          }}
          disabled={disabled}
        />
        {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
      </div>
    )
  }

  if (dataType === 'date') {
    return (
      <div data-testid={`add-equipment-field-${definition.code}`}>
        {labelEl}
        <Input
          type="date"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value || null)}
          disabled={disabled}
        />
      </div>
    )
  }

  // text / json / fallback
  return (
    <div data-testid={`add-equipment-field-${definition.code}`}>
      {labelEl}
      <Input
        type="text"
        value={value === null || value === undefined ? '' : String(value)}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
    </div>
  )
}

interface EnumFieldProps {
  org: string
  propUid: string
  value: unknown
  onChange: (v: unknown) => void
  disabled: boolean
  multiple: boolean
  locale: string
}

function EnumField({ org, propUid, value, onChange, disabled, multiple, locale }: EnumFieldProps) {
  const { data: enumValues = [] } = usePropertyEnumValues(org, propUid)

  const selected = useMemo<string[]>(() => {
    if (value === null || value === undefined) return []
    if (Array.isArray(value)) return value.map(String)
    return [String(value)]
  }, [value])

  if (!multiple) {
    return (
      <select
        value={selected[0] ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        disabled={disabled}
        className="block w-full px-3 py-2 border border-input bg-background text-foreground rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
      >
        <option value="">—</option>
        {enumValues.map((ev) => {
          const evName = ev.name as Record<string, string> | undefined
          const label = (evName && (evName[locale] || evName.en || evName.fr)) || ev.value
          return (
            <option key={ev.value} value={ev.value}>
              {label}
            </option>
          )
        })}
      </select>
    )
  }

  // multi-enum: checkbox grid
  return (
    <div className="grid grid-cols-2 gap-1">
      {enumValues.map((ev) => {
        const evName = ev.name as Record<string, string> | undefined
        const label = (evName && (evName[locale] || evName.en || evName.fr)) || ev.value
        const checked = selected.includes(ev.value)
        return (
          <label key={ev.value} className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={checked}
              disabled={disabled}
              onChange={(e) => {
                const next = e.target.checked
                  ? [...selected, ev.value]
                  : selected.filter((s) => s !== ev.value)
                onChange(next)
              }}
            />
            <span>{label}</span>
          </label>
        )
      })}
    </div>
  )
}
